import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Download, Share2 } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';

/**
 * Gets the VietQR image out of the dialog and into a chat with the payer.
 *
 * All three actions are offered wherever the browser supports them; only the
 * order changes. An earlier version showed Share *instead of* Copy whenever
 * sharing was available, on the theory that sharing is the phone answer and
 * copying the desktop one — but Chrome on macOS reports `canShare` true (it
 * bridges to the macOS share sheet), so desktop users lost the one-click path
 * to the clipboard and had to reach it through the sheet. Capability turned out
 * to be the wrong axis; what differs between a phone and a laptop is which
 * action you want *first*, not which ones exist.
 *
 * - **Touch device** — Share leads. The native sheet lists Zalo: one tap from
 *   here to the parent's chat. Copy is hidden, because a phone clipboard has no
 *   comfortable paste target.
 * - **Everything else** — Copy leads, because Zalo PC takes a paste directly,
 *   while sharing and downloading are both detours.
 *
 * Download is always last and always present: it is the one action that cannot
 * fail, and a teacher may simply want the file.
 *
 * Reading the image at all depends on the QR host serving a PNG with
 * `access-control-allow-origin: *`. When it stops, every button here breaks at
 * once, which is what img.vietqr.io did on 2026-11-01: `qrImageUrl` in the API's
 * vietqr.ts is the one place to change host, not this file.
 */

/** Probe with an empty File — `canShare` inspects the type, not the bytes. */
function deviceCanShareImages(): boolean {
  if (typeof navigator === 'undefined' || !navigator.canShare) return false;
  try {
    return navigator.canShare({ files: [new File([], 'qr.png', { type: 'image/png' })] });
  } catch {
    return false;
  }
}

function deviceCanCopyImages(): boolean {
  return typeof window !== 'undefined' && 'ClipboardItem' in window && !!navigator.clipboard?.write;
}

/** Touch-primary, i.e. a phone or tablet — not "small window on a laptop". */
function deviceIsTouchFirst(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;
}

async function fetchQrBlob(url: string): Promise<Blob> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`QR image fetch failed: ${res.status}`);
  const blob = await res.blob();
  // The QR host reports a failure as 200 text/html: sharing that as a .png
  // would look like success and hand the payer an unreadable photo.
  if (blob.type !== 'image/png') throw new Error(`QR image is not a PNG: ${blob.type}`);
  return blob;
}

export function QrActions({ qrUrl, paymentRef }: { qrUrl: string; paymentRef: string | null }) {
  const { t } = useTranslation('invoices');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  // Capability is a property of the device, not of this render — settle it once.
  const [canShare] = useState(deviceCanShareImages);
  const [canCopy] = useState(deviceCanCopyImages);
  const [touchFirst] = useState(deviceIsTouchFirst);

  const fileName = `LopHoc-QR-${(paymentRef || 'thanh-toan').replace(/[^\w-]+/g, '-')}.png`;

  const share = async () => {
    setBusy(true);
    try {
      const blob = await fetchQrBlob(qrUrl);
      await navigator.share({
        files: [new File([blob], fileName, { type: 'image/png' })],
        title: t('sendDialog.zalo.qrLabel'),
      });
    } catch (err) {
      // Dismissing the share sheet rejects with AbortError. That is the user
      // changing their mind, not a failure — saying so would be noise.
      if ((err as Error)?.name !== 'AbortError') toast.error(t('sendDialog.zalo.qrActionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    setBusy(true);
    try {
      // Safari only keeps the user-gesture if the ClipboardItem is constructed
      // synchronously with a *promise* value; awaiting the fetch first and then
      // writing loses the gesture and throws NotAllowedError.
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': fetchQrBlob(qrUrl) }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('sendDialog.zalo.qrActionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const blob = await fetchQrBlob(qrUrl);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      a.click();
      // A cross-origin href would have made `download` a no-op and navigated
      // away instead, which is why this goes through a blob at all.
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error(t('sendDialog.zalo.qrActionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const shareBtn = canShare && (
    <Button
      key="share"
      type="button"
      variant={touchFirst ? 'outline' : 'ghost'}
      size="sm"
      onClick={share}
      disabled={busy}
    >
      <Share2 className="mr-1.5 h-3.5 w-3.5" />
      {t('sendDialog.zalo.qrShare')}
    </Button>
  );

  // Hidden on touch devices: a phone clipboard has nowhere useful to paste.
  const copyBtn = canCopy && !touchFirst && (
    <Button key="copy" type="button" variant="outline" size="sm" onClick={copy} disabled={busy}>
      {copied ? <Check className="mr-1.5 h-3.5 w-3.5 text-green-600" /> : <ImageCopyIcon />}
      {copied ? t('sendDialog.zalo.qrCopied') : t('sendDialog.zalo.qrCopy')}
    </Button>
  );

  return (
    <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
      {touchFirst ? [shareBtn, copyBtn] : [copyBtn, shareBtn]}

      <Button type="button" variant="ghost" size="sm" onClick={download} disabled={busy}>
        <Download className="mr-1.5 h-3.5 w-3.5" />
        {t('sendDialog.zalo.qrDownload')}
      </Button>
    </div>
  );
}

/** lucide's `Copy` is two documents; this one reads as an image. */
function ImageCopyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mr-1.5 h-3.5 w-3.5"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="14" height="14" rx="2" />
      <circle cx="7.5" cy="7.5" r="1.5" />
      <path d="m3 13 3.5-3.5 3 3L13 9l4 4" />
      <path d="M21 8v11a2 2 0 0 1-2 2H8" />
    </svg>
  );
}
